/**
 * AppRouter — the platform's entire route table.
 *
 * Authenticated routes are nested under layout routes (AuthLayout /
 * RbacLayout / SuperAdminLayout) that mount <AppLayout /> (nav rail +
 * <Outlet />) INSIDE the guard, so an unauthorized user is redirected
 * before any chrome renders. Public/auth/legal/invite pages stay bare
 * and render whatever chrome they need themselves.
 *
 * Adding a page: pick the guard group (auth-only, a permission group, or
 * super-admin), add a <Route> inside it. Do not render <Navbar /> in the
 * page — AppLayout owns it.
 */
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertProvider } from '../contexts/AlertContext';
import { DrillDownProvider } from '../contexts/DrillDownContext';
import { AppLayout } from '../components/layout/AppLayout';
import { Login } from '../pages/Login';
import { Register } from '../pages/Register';
import { ForgotPassword } from '../pages/ForgotPassword';
import { ResetPassword } from '../pages/ResetPassword';
import { PrivacyPolicy } from '../pages/PrivacyPolicy';
import { TermsOfService } from '../pages/TermsOfService';
import { ContactSupport } from '../pages/ContactSupport';
import { CarrierDashboard } from '../pages/CarrierDashboard';
import { BrokerDashboard } from '../pages/BrokerDashboard';
import CarrierComparePage from '../pages/CarrierComparePage';
import BulkScreeningPage from '../pages/BulkScreeningPage';
import { AdminDashboard } from '../pages/AdminDashboard';
import { SystemMetricsPage } from '../pages/SystemMetricsPage';
import { LandingPage } from '../pages/LandingPage';
import VINLookup from '../pages/VINLookup';
import StatsDashboard from '../pages/StatsDashboard';
import AlertsPage from '../pages/AlertsPage';
import AlertPreferencesPage from '../pages/AlertPreferencesPage';
import WatchlistPage from '../pages/WatchlistPage';
import MonitoredCarriersPage from '../pages/MonitoredCarriersPage';
import VinMatchesPage from '../pages/VinMatchesPage';
import MergeCarriersPage from '../pages/MergeCarriersPage';
import MarketIntelligence from '../pages/MarketIntelligence';
import GeographicIntelligence from '../pages/GeographicIntelligence';
import InsuranceIntelligence from '../pages/InsuranceIntelligence';
import ShipperAuditPage from '../pages/ShipperAuditPage';
import DnuAnalyticsPage from '../pages/DnuAnalyticsPage';
import TagsManagementPage from '../pages/TagsManagementPage';
import SMTPSettings from '../pages/admin/SMTPSettings';
import SuperAdminDashboard from '../pages/SuperAdminDashboard';
import UserManagement from '../pages/UserManagement';
import CompanyManagement from '../pages/CompanyManagement';
import AuditLogViewer from '../pages/AuditLogViewer';
import { RoleManagement } from '../pages/admin/RoleManagement';
import { RegistrationRequests } from '../pages/admin/RegistrationRequests';
import Unauthorized from '../pages/Unauthorized';
import NotFound from '../pages/NotFound';
import ComingSoon from '../pages/ComingSoon';
import { LoadingPage } from '../components/shared/LoadingSpinner';
import DrillDownModal from '../components/DrillDownModal';
import { ProtectedRoute as RBACProtectedRoute, SuperAdminRoute } from '../components/rbac';

const RiskDigest = lazy(() => import('../pages/RiskDigest'));
const CarrierScorecardPage = lazy(() => import('../pages/CarrierScorecardPage'));
const CarrierSignalsExplorer = lazy(() => import('../pages/CarrierSignalsExplorer'));
const PeerBenchmarkPage = lazy(() => import('../pages/PeerBenchmark'));
const CarrierScorecardHistoryPage = lazy(() => import('../pages/CarrierScorecardHistory'));
const CarrierWatchlistPage = lazy(() => import('../pages/CarrierWatchlist'));
const WatchlistDigestHistoryPage = lazy(() => import('../pages/WatchlistDigestHistory'));
const CarrierComparisonPage = lazy(() => import('../pages/CarrierComparison'));
const ShipperProfileV2Page = lazy(() => import('../pages/ShipperProfileV2'));
const OnboardingConsole = lazy(() => import('../pages/OnboardingConsole'));
const OnboardingInviteAccept = lazy(() => import('../pages/OnboardingInviteAccept'));
const OnboardingInviteStatus = lazy(() => import('../pages/OnboardingInviteStatus'));
const OnboardingPacketReview = lazy(() => import('../pages/OnboardingPacketReview'));
const OnboardingActivationConsole = lazy(() => import('../pages/OnboardingActivationConsole'));
const OnboardingInviteMetrics = lazy(() => import('../pages/OnboardingInviteMetrics'));
const OnboardingDocumentExpiry = lazy(() => import('../pages/OnboardingDocumentExpiry'));
const OnboardingInviteNotes = lazy(() => import('../pages/onboarding/OnboardingInviteNotes'));
const OnboardingBulkImport = lazy(() => import('../pages/OnboardingBulkImport'));
const CarrierOnboardingHub = lazy(() => import('../pages/onboarding/CarrierOnboardingHub'));
const OnboardingInviteAdmin = lazy(() => import('../pages/OnboardingInviteAdmin'));
const CarriersByLocation = lazy(() => import('../pages/CarriersByLocation'));
const CarriersByShipper = lazy(() => import('../pages/CarriersByShipper'));
const RequestSignup = lazy(() => import('../pages/RequestSignup'));
const CompleteSignup = lazy(() => import('../pages/CompleteSignup'));

// v1 ShipperProfilePage removed 2026-05-21; /shipper-audit/:name now redirects to v2
const ShipperProfileV1Redirect: React.FC = () => {
  const { shipperName } = useParams<{ shipperName: string }>();
  return <Navigate to={`/shipper-audit-v2/${encodeURIComponent(shipperName ?? '')}`} replace />;
};

// Basic Protected Route Component (for non-RBAC routes)
const BasicProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <LoadingPage />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Layout wrappers: guard first, app chrome second. Nested routes render in
// AppLayout's <Outlet /> only after the guard passes.
const AuthLayout: React.FC = () => (
  <BasicProtectedRoute>
    <AppLayout />
  </BasicProtectedRoute>
);

const RbacLayout: React.FC<{ permission?: string; permissions?: string[] }> = ({ permission, permissions }) => (
  <RBACProtectedRoute permission={permission} permissions={permissions}>
    <AppLayout />
  </RBACProtectedRoute>
);

const SuperAdminLayout: React.FC = () => (
  <SuperAdminRoute>
    <AppLayout />
  </SuperAdminRoute>
);

// Dashboard Router - routes based on user type
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.userType) {
    case 'carrier':
      return <CarrierDashboard />;
    case 'broker':
      return <BrokerDashboard />;
    case 'admin':
    case 'government':
      return <AdminDashboard />;
    default:
      return <CarrierDashboard />;
  }
};

const Lazy: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingPage />}>{children}</Suspense>
);

export const AppRouter: React.FC = () => {
  return (
    <AlertProvider>
      <DrillDownProvider syncWithUrl={true}>
        <Routes>
          {/* ============ Bare routes (no app chrome) ============ */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Lazy><RequestSignup /></Lazy>} />
          <Route path="/complete-signup" element={<Lazy><CompleteSignup /></Lazy>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/contact" element={<ContactSupport />} />
          <Route path="/onboarding/invite/:token" element={<Lazy><OnboardingInviteAccept /></Lazy>} />
          <Route path="/onboarding/invite/:token/status" element={<Lazy><OnboardingInviteStatus /></Lazy>} />
          <Route path="/shipper-audit/:shipperName" element={<ShipperProfileV1Redirect />} />

          {/* ============ Authenticated, no specific permission ============ */}
          <Route element={<AuthLayout />}>
            <Route path="/dashboard" element={<DashboardRouter />} />
            <Route path="/carrier-search" element={<CarrierDashboard />} />
            <Route path="/carrier/:dotNumber" element={<CarrierDashboard />} />
            <Route path="/carriers-by-location" element={<Lazy><CarriersByLocation /></Lazy>} />
            <Route path="/carriers-by-shipper" element={<Lazy><CarriersByShipper /></Lazy>} />
            <Route path="/carrier-compare" element={<CarrierComparePage />} />
            <Route path="/carrier-comparison" element={<Lazy><CarrierComparisonPage /></Lazy>} />
            <Route path="/risk-digest" element={<Lazy><RiskDigest /></Lazy>} />
            <Route path="/carrier-scorecard" element={<Lazy><CarrierScorecardPage /></Lazy>} />
            <Route path="/carrier-signals" element={<Lazy><CarrierSignalsExplorer /></Lazy>} />
            <Route path="/peer-benchmark" element={<Lazy><PeerBenchmarkPage /></Lazy>} />
            <Route path="/peer-benchmark/:dotNumber" element={<Lazy><PeerBenchmarkPage /></Lazy>} />
            <Route path="/scorecard-history" element={<Lazy><CarrierScorecardHistoryPage /></Lazy>} />
            <Route path="/scorecard-history/:dotNumber" element={<Lazy><CarrierScorecardHistoryPage /></Lazy>} />
            <Route path="/carrier-watchlist" element={<Lazy><CarrierWatchlistPage /></Lazy>} />
            <Route path="/watchlist-digest-history" element={<Lazy><WatchlistDigestHistoryPage /></Lazy>} />
            <Route path="/bulk-screening" element={<BulkScreeningPage />} />
            <Route path="/vin-matches" element={<VinMatchesPage />} />
            <Route path="/merge" element={<MergeCarriersPage />} />
            <Route path="/onboarding" element={<Lazy><OnboardingConsole /></Lazy>} />
            <Route path="/onboarding/packet-review" element={<Lazy><OnboardingPacketReview /></Lazy>} />
            <Route path="/onboarding/activation" element={<Lazy><OnboardingActivationConsole /></Lazy>} />
            <Route path="/onboarding/metrics" element={<Lazy><OnboardingInviteMetrics /></Lazy>} />
            <Route path="/onboarding/document-expiry" element={<Lazy><OnboardingDocumentExpiry /></Lazy>} />
            <Route path="/onboarding/bulk-import" element={<Lazy><OnboardingBulkImport /></Lazy>} />
            <Route path="/onboarding/hub" element={<Lazy><CarrierOnboardingHub /></Lazy>} />
            <Route path="/onboarding/invite-admin" element={<Lazy><OnboardingInviteAdmin /></Lazy>} />
            <Route path="/onboarding/notes" element={<Lazy><OnboardingInviteNotes /></Lazy>} />
            <Route path="/broker" element={<ComingSoon title="Broker Dashboard" description="The broker-specific dashboard is coming soon." />} />
          </Route>

          {/* ============ RBAC-guarded groups ============ */}
          <Route element={<RbacLayout permissions={['admin.users.view', 'admin.companies.view', 'admin.roles.view']} />}>
            <Route path="/admin" element={<AdminDashboard />} />
          </Route>
          <Route element={<RbacLayout permission="vins.lookup" />}>
            <Route path="/vin-lookup" element={<VINLookup />} />
          </Route>
          <Route element={<RbacLayout permission="analytics.view" />}>
            <Route path="/statistics" element={<StatsDashboard />} />
            <Route path="/dnu-analytics" element={<DnuAnalyticsPage />} />
          </Route>
          <Route element={<RbacLayout permission="analytics.market_intelligence" />}>
            <Route path="/market-intelligence" element={<MarketIntelligence />} />
            <Route path="/geographic" element={<GeographicIntelligence />} />
            <Route path="/insurance-intelligence" element={<InsuranceIntelligence />} />
          </Route>
          <Route element={<RbacLayout permission="shippers.audit" />}>
            <Route path="/shipper-audit" element={<ShipperAuditPage />} />
          </Route>
          <Route element={<RbacLayout permission="shippers.view" />}>
            <Route path="/shipper-audit-v2/:shipperName" element={<Lazy><ShipperProfileV2Page /></Lazy>} />
          </Route>
          <Route element={<RbacLayout permission="inspections.view" />}>
            <Route path="/tags-management" element={<TagsManagementPage />} />
          </Route>
          <Route element={<RbacLayout permission="monitoring.alerts.view" />}>
            <Route path="/alerts" element={<AlertsPage />} />
          </Route>
          <Route element={<RbacLayout permission="monitoring.alerts.manage" />}>
            <Route path="/alert-preferences" element={<AlertPreferencesPage />} />
          </Route>
          <Route element={<RbacLayout permission="monitoring.watchlist.view" />}>
            <Route path="/watchlist" element={<WatchlistPage />} />
            <Route path="/monitored" element={<MonitoredCarriersPage />} />
          </Route>
          <Route element={<RbacLayout permission="admin.system.settings" />}>
            <Route path="/admin/smtp-settings" element={<SMTPSettings />} />
          </Route>
          <Route element={<RbacLayout permission="admin.users.view" />}>
            <Route path="/admin/registration-requests" element={<RegistrationRequests />} />
            <Route path="/super-admin/users" element={<UserManagement />} />
          </Route>
          <Route element={<RbacLayout permission="admin.system.stats" />}>
            <Route path="/admin/system-metrics" element={<SystemMetricsPage />} />
          </Route>
          <Route element={<RbacLayout permission="admin.companies.view" />}>
            <Route path="/super-admin/companies" element={<CompanyManagement />} />
          </Route>
          <Route element={<RbacLayout permission="admin.system.audit" />}>
            <Route path="/super-admin/audit-log" element={<AuditLogViewer />} />
          </Route>
          <Route element={<RbacLayout permission="admin.roles.view" />}>
            <Route path="/super-admin/roles" element={<RoleManagement />} />
          </Route>

          {/* ============ Super admin ============ */}
          <Route element={<SuperAdminLayout />}>
            <Route path="/super-admin" element={<SuperAdminDashboard />} />
            <Route path="/super-admin/settings" element={<ComingSoon title="System Settings" description="System configuration settings are coming soon." />} />
            <Route path="/super-admin/database" element={<ComingSoon title="Database Management" description="Database management tools are coming soon." />} />
          </Route>

          {/* ============ Aliases ============ */}
          <Route path="/vin-intelligence" element={<Navigate to="/vin-lookup" replace />} />
          <Route path="/analytics" element={<Navigate to="/statistics" replace />} />
          <Route path="/stats" element={<Navigate to="/statistics" replace />} />
          <Route path="/shipper-intelligence" element={<Navigate to="/shipper-audit" replace />} />
          <Route path="/monitored-carriers" element={<Navigate to="/monitored" replace />} />
          <Route path="/merge-carriers" element={<Navigate to="/merge" replace />} />
          <Route path="/super-admin/logs" element={<Navigate to="/super-admin/audit-log" replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        <DrillDownModal />
      </DrillDownProvider>
    </AlertProvider>
  );
};
